import { Flex, Text, Button, TextField } from '@servicetitan/anvil2';
import { ApplicationDataStore } from '../application';
import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDependencies } from '@servicetitan/react-ioc';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import {
    FinancingApi,
    AuthenticationChallengeCompleteRequest,
    CustomerAuthChallengeStatus,
    CustomerAuthCodeChallengeState,
} from '@servicetitan/financing-api-client';
import { useCustomerAuthStartChallenge } from './useCustomerAuthStartChallenge';
import ReCAPTCHA from 'react-google-recaptcha';
import { useGreenSkyRecaptcha } from './useGreenSkyRecaptcha';
import { StepWrapper } from './StepWrapper';
import { Buttons } from './Buttons';

const maskPhone = (phone: string | undefined): string => {
    return phone?.replace(/\d(?=(?:\D*\d){4})/g, '*') ?? '';
};

const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

interface VerificationStepProps {
    challengeStatus: CustomerAuthChallengeStatus | null;
    onSuccess: () => void;
    onChallengeStatusUpdate: (challengeData: CustomerAuthChallengeStatus) => void;
    onBack?: () => void;
}

export const VerificationStep = ({
    challengeStatus,
    onSuccess,
    onChallengeStatusUpdate,
    onBack,
}: VerificationStepProps) => {
    const { applicationData } = ApplicationDataStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const [apiClient] = useDependencies(FinancingApi);
    const [coolDownTimer, setCoolDownTimer] = useState(0);
    const [expirationTimer, setExpirationTimer] = useState(0);
    const [pin, setPin] = useState<string>('');
    const [error, setError] = useState<string | undefined>(undefined);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const currentChallengeIdRef = useRef<string | null>(null);

    const completeMutation = useMutation({
        mutationKey: ['customerAuthCompleteChallenge'],
        mutationFn: (pin: string) => {
            if (!challengeStatus?.challengeId) {
                throw new Error('No challenge ID available');
            }

            const request: AuthenticationChallengeCompleteRequest = {
                tenantId: setupData?.tenantId,
                buId: setupData?.buId,
                challengeId: challengeStatus.challengeId,
                pin,
            };

            return apiClient.customerAuthCompleteChallenge(
                setupData?.provider ?? '',
                challengeStatus.challengeId,
                request,
            );
        },
        onSuccess: ({ data }: { data: CustomerAuthChallengeStatus }) => {
            onChallengeStatusUpdate(data);
            setError(undefined);
            if (data.status === CustomerAuthCodeChallengeState.Completed) {
                onSuccess();
            } else if (data.status === CustomerAuthCodeChallengeState.ExpiredOrFailed) {
                setError('The code has expired. Please request a new code.');
            } else if (data.status === CustomerAuthCodeChallengeState.Invalid) {
                setError('The code is invalid. Please try again.');
            }
        },
    });

    const resendMutation = useCustomerAuthStartChallenge({
        onSuccess: challengeData => {
            onChallengeStatusUpdate(challengeData);
        },
        mutationKey: ['customerAuthStartChallengeResend'],
        recaptchaRef,
    });

    const createTimerEffect = (
        timer: number,
        setTimer: (value: (prev: number) => number) => void,
    ) => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer(prev => Math.max(0, prev - 1));
            }, 1000);

            return () => clearInterval(interval);
        }
    };

    useEffect(() => {
        return createTimerEffect(coolDownTimer, setCoolDownTimer);
    }, [coolDownTimer]);

    useEffect(() => {
        return createTimerEffect(expirationTimer, setExpirationTimer);
    }, [expirationTimer]);

    // Initialize timers from challenge status only when challenge ID changes
    useEffect(() => {
        if (challengeStatus && challengeStatus.challengeId !== currentChallengeIdRef.current) {
            currentChallengeIdRef.current = challengeStatus.challengeId!;

            setCoolDownTimer(
                challengeStatus.cooldownInSeconds > 0 ? challengeStatus.cooldownInSeconds : 0,
            );

            // Calculate expiration timer from validUntilUtc
            if (challengeStatus.validUntilUtc) {
                const validUntil = new Date(challengeStatus.validUntilUtc);
                const now = new Date();
                const secondsUntilExpiration = Math.max(
                    0,
                    Math.floor((validUntil.getTime() - now.getTime()) / 1000),
                );
                setExpirationTimer(secondsUntilExpiration);
            } else {
                setExpirationTimer(0);
            }
        }
    }, [challengeStatus]);

    const handleSubmit = () => {
        completeMutation.mutate(pin);
    };

    const handleResend = () => {
        const phone = applicationData.externalApplication.primaryPhone;
        const email = applicationData.externalApplication.email;

        if (phone && email) {
            resendMutation.mutate({ phone, email });
        }
    };

    const isSubmitDisabled =
        pin.length !== 4 || completeMutation.isPending || resendMutation.isPending;

    return (
        <StepWrapper>
            <div style={{ maxWidth: '514px' }}>
                <Flex gap="3" direction="column" flex={1}>
                    <Text variant="headline" el="h2">
                        Enter your code to continue.
                    </Text>
                    <Text variant="body" size="medium">
                        A code has been sent to{' '}
                        {maskPhone(applicationData.externalApplication.primaryPhone)} and{' '}
                        {applicationData.externalApplication.email}. The code will expire in{' '}
                        {formatTime(expirationTimer)}.
                    </Text>
                    <Flex gap="3" flex={1} direction="row" justifyContent="space-between">
                        <Text variant="body" size="medium">
                            <b>{formatTime(coolDownTimer)}</b>
                        </Text>
                        <Button
                            appearance="secondary"
                            size="small"
                            disabled={coolDownTimer > 0 || resendMutation.isPending}
                            onClick={handleResend}
                            loading={resendMutation.isPending}
                        >
                            Resend Code
                        </Button>
                    </Flex>
                    <TextField
                        name="pin"
                        label="4 Digit Code"
                        required
                        maxLength={4}
                        inputMode="numeric"
                        value={pin}
                        onChange={e => {
                            const value = e.target.value.replace(/\D/g, '');
                            setPin(value);
                        }}
                        error={error}
                    />
                    <Button
                        appearance="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                        loading={completeMutation.isPending}
                        style={{ width: '100%' }}
                    >
                        Submit
                    </Button>
                </Flex>
            </div>
            {useGreenSkyRecaptcha(recaptchaRef)}
            <Buttons onBack={onBack} hideContinueButton />
        </StepWrapper>
    );
};
